import httpsCall from '@/services/httpsCall';
import type { VendorDetails } from '@/types/types';
import logger from '@/utils/logger';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ListResponse {
  content: any[];
  page: {
    totalPages: number;
    totalElements: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
    paged: boolean;
  };
}

interface UpdateVendorInput {
  id: string;
  name: string;
  accountNumber: string;
  phone: string;
}

// * ====================== Fetch All Store Owners For Map ====================== *
const fetchAdminData = async () => {
  const response = await httpsCall.get(`/payment/dashboard-stats`);
  return response?.data?.data;
};

export const useFetchAdminData = () => {
  return useQuery({
    queryKey: ['admin-summery-data'],
    queryFn: fetchAdminData,
  });
};

// * ====================== Fetch All Store Owners For Map ====================== *
const fetchAllOwnersForMap = async () => {
  const response = await httpsCall.get(`/superadmin/dashboard/all-locations`);
  return response?.data?.data;
};

export const useFetchAllOwnersForMap = () => {
  return useQuery({
    queryKey: ['owners-for-map'],
    queryFn: fetchAllOwnersForMap,
  });
};

// * ====================== Fetch All Owner Dashboard ====================== *
const fetchAllownerdashboard = async () => {
  const response = await httpsCall.get(`/owner/dashboard/summary`);
  return response?.data?.data;
};

export const useFetchAllownerdashboard = () => {
  return useQuery({
    queryKey: ['owners-dashboard-summary'],
    queryFn: fetchAllownerdashboard,
  });
};

// * ====================== Fetch All Import Data ====================== *
const fetchFailedImportCountForOwner = async () => {
  const response = await httpsCall.get(`/owner/dashboard/failed-import-count`);
  return response?.data?.data;
};

export const useFetchFailedImportCountForOwner = () => {
  return useQuery({
    queryKey: ['owners-dashboard-failed-import'],
    queryFn: fetchFailedImportCountForOwner,
  });
};

// * ====================== Fetch Subscription Counts [By current year] ====================== *
const fetchSubscriptionCounts = async () => {
  const response = await httpsCall.get(`/payment/subscriber-by-month`);
  return response?.data?.data;
};

export const useSubscriptionCounts = () => {
  return useQuery({
    queryKey: ['subscription-counts'],
    queryFn: fetchSubscriptionCounts,
  });
};

// * ====================== Fetch Total Failed Imports [Current Month] ====================== *
const fetchFailedImportCount = async () => {
  const response = await httpsCall.get(`/superadmin/import-failed-count`);
  return response?.data?.data;
};

export const useFetchFailedImportCount = () => {
  return useQuery({
    queryKey: ['total-failed-import-count'],
    queryFn: fetchFailedImportCount,
  });
};

const fetchRecentQRScanActivity = async () => {
  const response = await httpsCall.get(`/owner/dashboard/reset-scan`);
  return response?.data?.data;
};

export const useFetchRecentQRScanActivity = () => {
  return useQuery({
    queryKey: ['recent-qr-scan-activity'],
    queryFn: () => fetchRecentQRScanActivity(),
  });
};

const fetchMostScannedProducts = async () => {
  const response = await httpsCall.get(`/owner/dashboard/top-5-scanned-products`);
  return response?.data?.data;
};

export const useFetchMostScannedProducts = () => {
  return useQuery({
    queryKey: ['most-scanned-products'],
    queryFn: () => fetchMostScannedProducts(),
  });
};

// ## ========================== fetch active vendors ================================
export const fetchActiveVendors = async ({
  page = 0,
  size = 10,
  keyword = '',
}: {
  page?: number;
  size?: number;
  keyword?: string;
}): Promise<ListResponse> => {
  try {
    const response = await httpsCall.get(
      `/owner/dashboard/vendors-list?page=${page}&size=${size}&Keyword=${encodeURIComponent(keyword)}`
    );
    return response.data?.data as ListResponse;
  } catch (error: any) {
    logger.error('Error fetching vendors:', error);
    throw error;
  }
};

export const useFetchActiveVendors = (
  page: number = 0,
  size: number = 10,
  keyword: string = ''
) => {
  return useQuery<ListResponse, Error>({
    queryKey: ['active-vendors', page, size, keyword],
    queryFn: () => fetchActiveVendors({ page, size, keyword }),
    placeholderData: (previousData) => previousData,
    // staleTime: timeConverter(20, 'minute'),
  });
};

// ## ========================== fetch scanned products ================================
export const fetchScannedProducts = async ({
  page = 0,
  size = 10,
}: {
  page?: number;
  size?: number;
}): Promise<ListResponse> => {
  try {
    const response = await httpsCall.get(
      `/owner/dashboard/scanned-products?page=${page}&size=${size}`
    );
    return response.data?.data as ListResponse;
  } catch (error: any) {
    logger.error('Error fetching vendors:', error);
    throw error;
  }
};

export const useFetchScannedProducts = (page: number = 0, size: number = 10) => {
  return useQuery<ListResponse, Error>({
    queryKey: ['scanned-products', page, size],
    queryFn: () => fetchScannedProducts({ page, size }),
    placeholderData: (previousData) => previousData,
    // staleTime: timeConverter(20, 'minute'),
  });
};

// ## ========================== fetch sample requests products ================================
export const fetchSampleRequest = async ({
  page = 0,
  size = 10,
}: {
  page?: number;
  size?: number;
}): Promise<ListResponse> => {
  try {
    const response = await httpsCall.get(`/owner/request-sample?page=${page}&size=${size}`);
    return response.data?.data as ListResponse;
  } catch (error: any) {
    logger.error('Error fetching faild-imports:', error);
    throw error;
  }
};

export const useFetchSampleRequest = (page: number = 0, size: number = 10) => {
  return useQuery<ListResponse, Error>({
    queryKey: ['faild-import', page, size],
    queryFn: () => fetchSampleRequest({ page, size }),
    placeholderData: (previousData) => previousData,
    // staleTime: timeConverter(20, 'minute'),
  });
};

// ## ========================== fetch faild-imports ================================
export const fetchFailedImports = async ({
  page = 0,
  size = 10,
  sortBy = '',
  sortDir = '',
}: {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc' | '';
}): Promise<ListResponse> => {
  try {
    const response = await httpsCall.get(
      `owner/dashboard/failed-import-list?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`
    );
    return response.data?.data as ListResponse;
  } catch (error: any) {
    logger.error('Error fetching vendors:', error);
    throw error;
  }
};

export const useFetchFailedImports = (
  page: number = 0,
  size: number = 10,
  sortBy: string = '',
  sortDir: 'asc' | 'desc' | '' = ''
) => {
  return useQuery<ListResponse, Error>({
    queryKey: ['sample-request', page, size, sortBy, sortDir],
    queryFn: () => fetchFailedImports({ page, size, sortBy, sortDir }),
    placeholderData: (previousData) => previousData,
    // staleTime: timeConverter(20, 'minute'),
  });
};

// ## ========================== fetch Discontinued Products ================================
export const fetchDiscontinuedProducts = async ({
  page = 0,
  size = 10,
  keyword = '',
}: {
  page?: number;
  size?: number;
  keyword?: string;
}): Promise<ListResponse> => {
  try {
    const response = await httpsCall.get(
      `/owner/dashboard/discontinued-products?page=${page}&size=${size}&Keyword=${encodeURIComponent(keyword)}`
    );
    return response.data?.data as ListResponse;
  } catch (error: any) {
    logger.error('Error fetching vendors:', error);
    throw error;
  }
};

export const useFetchDiscontinuedProducts = (
  page: number = 0,
  size: number = 10,
  keyword: string = ''
) => {
  return useQuery<ListResponse, Error>({
    queryKey: ['discontinued-products', page, size, keyword],
    queryFn: () => fetchDiscontinuedProducts({ page, size, keyword }),
    placeholderData: (previousData) => previousData,
    // staleTime: timeConverter(20, 'minute'),
  });
};

// -------------------------- fetch single active vendor ------------------

const fetchSingleActiveVendor = async (vendorId: string): Promise<VendorDetails | null> => {
  const response = await httpsCall.get(`/owner/dashboard/get-vendorInfo/${vendorId}`);
  return response?.data?.data;
};

export const useFetchSingleActiveVendor = (vendorId?: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['single-active-vendor', vendorId],
    queryFn: () => fetchSingleActiveVendor(vendorId!),
    enabled: !!vendorId && enabled,
    retry: false,
    // staleTime: timeConverter(10, 'minute'),
  });
};

// -------------------------- update active vendor ------------------
export const updateActiveVendor = async (updateInputData: UpdateVendorInput) => {
  const response = await httpsCall.post(`/owner/dashboard/add-vendorInfo`, {
    accountNumber: updateInputData?.accountNumber,
    phone: updateInputData?.phone,
    vendors: {
      id: updateInputData?.id,
    },
  });
  return response.data;
};

export const useUpdateActiveVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateActiveVendor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['vendor'], exact: false });
    },
  });
};
